import { useMemo, useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import {
  alpha,
  Chip,
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material'
import { useIntl } from 'react-intl'

import { SparckRewardsListFilterInfo } from '..'
// import { OrganizationalRewardCard } from 'components'
import { WishType } from 'enums/wish'
import { t } from 'i18n'
import { getRewardName } from 'utils/reward'
import CorporateRewardCard from 'components/CorporateRewardCard'
import { API } from 'core/requests'

export default function SparckCorporateRewardsList({
  sparckState,
  onSelectReward,
  isDialogView = false,
  selectedCatalogId
}) {
  const theme = useTheme()
  const intl = useIntl()
  const REWARDS_PREVIEW_COUNT = 5
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [favoritesOnly, setFavoritesOnly] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchText])

  const favoritesOnlyChips = [
    {
      id: 'allRewards',
      label: t('sparcks.sparck.allRewards'),
      onClick: () => setFavoritesOnly(false),
      isSelected: !favoritesOnly,
    },
    {
      id: 'favoritesOnly',
      label: t('sparcks.sparck.favoritesOnly'),
      onClick: () => setFavoritesOnly(true),
      isSelected: !!favoritesOnly,
    },
  ]

  const individualToRecognizeOrganizationalWishesIds = useMemo(
    () =>
      sparckState?.individual_to_recognize?.wishlist?.wishes
        ?.filter(wish => wish.wishable_type === WishType.Company)
        ?.map(wish => +wish.wishable_id),
    [sparckState.individual_to_recognize],
  )


  const { data: allRewards = [], isLoading } = useQuery(
    [
      'getAllCorporateRewards',
      sparckState?.event?.maximum_reward_amount_for_each_recognition,
      debouncedSearchText,
      selectedCatalogId || sparckState?.CorporateRewardsCategory?.[1]?.id,
      sparckState.users_to_recognize.map(user => +user.id).join(','),
    ],
    () =>
      API.createOrUpdate('surveyRewardIntentions', {
        price: +sparckState?.event?.maximum_reward_amount_for_each_recognition,
        hobbies: debouncedSearchText,
        catalog_id: selectedCatalogId || sparckState?.CorporateRewardsCategory?.[1]?.id,
        user_id: sparckState.users_to_recognize.map(user => +user.id),
        isDialogView: true,
        limit: 100,
        page: 1,
        per_page: 100,
      }),
    {
      enabled: !!sparckState?.event?.maximum_reward_amount_for_each_recognition && !!(selectedCatalogId || sparckState?.CorporateRewardsCategory?.[1]?.id),
      select: (response) => response?.data || response || [],
    }
  )

  const filteredRewards = useMemo(() => {
    let newRewards = allRewards

    if (favoritesOnly && individualToRecognizeOrganizationalWishesIds?.length) {
      newRewards = newRewards.filter(reward =>
        individualToRecognizeOrganizationalWishesIds.includes(reward.id),
      )
    }

    if (searchText) {
      newRewards = newRewards.filter(reward =>
        getRewardName(reward)
          ?.toLowerCase()
          ?.includes(searchText.toLowerCase()),
      )
    }

    return newRewards
  }, [allRewards, searchText, favoritesOnly, individualToRecognizeOrganizationalWishesIds])

  if (isLoading) {
    return (
      <Grid container justifyContent='center' spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems='center' sx={{ mb: 3 }}>
            <Grid item xs={12} sm>
              <TextField
                id='rewardsSearch'
                variant='outlined'
                value={searchText}
                onChange={(event: any) => setSearchText(event.target.value)}
                label={t('sparcks.sparck.rewardsSearch')}
                placeholder={intl.formatMessage({
                  id: 'sparcks.sparck.searchForAReward',
                })}
                fullWidth
              />
            </Grid>

            <SparckRewardsListFilterInfo
              maxAmount={
                sparckState?.event?.maximum_reward_amount_for_each_recognition
              }
            />

            {individualToRecognizeOrganizationalWishesIds?.length > 0 && (
              <Grid item xs={12} sm='auto'>
                {favoritesOnlyChips.map(chip => (
                  <Chip
                    key={chip.id}
                    onClick={chip.onClick}
                    label={chip.label}
                    sx={{
                      mb: 0,
                      ...(chip.isSelected && {
                        outline: `1.5px solid ${theme.palette.primary.main}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                      }),
                    }}
                  />
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container justifyContent='center' spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems='center' sx={{ mb: 3 }}>
          <Grid item xs={12} sm>
            <TextField
              id='rewardsSearch'
              variant='outlined'
              value={searchText}
              onChange={(event: any) => setSearchText(event.target.value)}
              label={t('sparcks.sparck.rewardsSearch')}
              placeholder={intl.formatMessage({
                id: 'sparcks.sparck.searchForAReward',
              })}
              fullWidth
            />
          </Grid>

          <SparckRewardsListFilterInfo
            maxAmount={
              sparckState?.event?.maximum_reward_amount_for_each_recognition
            }
          />

          {individualToRecognizeOrganizationalWishesIds?.length > 0 && (
            <Grid item xs={12} sm='auto'>
              {favoritesOnlyChips.map(chip => (
                <Chip
                  key={chip.id}
                  onClick={chip.onClick}
                  label={chip.label}
                  sx={{
                    mb: 0,
                    ...(chip.isSelected && {
                      outline: `1.5px solid ${theme.palette.primary.main}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    }),
                  }}
                />
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      {filteredRewards.length > 0 ? (
        filteredRewards.map((reward, index) => {
          return (
            <Grid
              item
              xs={12}
              className='custom_topProductDisplay'
              sm={index <= 1 ? 6 : 4}
              lg={isDialogView ? 3 : (filteredRewards?.length < REWARDS_PREVIEW_COUNT ? 3 : true)}
              key={reward.id}
            >
              <CorporateRewardCard
                reward={reward}
                onClick={onSelectReward}
                {...(reward.id === 'organizationalRewards' && {
                  cardSx: {
                    height: 1,
                  },
                })}
              />
            </Grid>
          )
        })
      ) : (
        <Grid item xs={12} sx={{ my: 3 }}>
          <Typography variant='body1' align='center' color='text.secondary'>
            {t('sparcks.sparck.noRewardsFound')}
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}
